import { Link, useSearchParams } from "react-router-dom";
import { Phone, Mail, MapPin, Globe, MessageCircle, Handshake } from "lucide-react";
import {
  COMPANY, PHONE_DISPLAY, PHONE_TEL, EMAIL, WEBSITE, ADDRESS, waLink, FAQS,
} from "../data/site";
import { EnquiryForm } from "../components/EnquiryForm";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../hooks/usePageMeta";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

export default function Contact() {
  usePageMeta(
    "Contact Us | ORGANIKTHINGS LLP",
    "Contact ORGANIKTHINGS LLP for bulk pricing, dealership and white-label enquiries. Call +91 97536 07101, email reachorganicthings@gmail.com or message us on WhatsApp. Indore, Madhya Pradesh."
  );
  const [params] = useSearchParams();
  const product = params.get("product") || "";

  return (
    <main data-testid="contact-page">
      <section className="bg-[#f4f1ea] py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold tracking-wider uppercase text-[#2d6a4f] mb-3">Contact Us</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0d1f18] leading-tight">
            Let's talk business
          </h1>
          <p className="mt-5 text-base md:text-lg text-[#2d3748] leading-relaxed max-w-2xl">
            Reach out for bulk pricing, dealership or white-label partnership
            enquiries — we respond on phone, email and WhatsApp.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <div className="bg-[#faf9f6] rounded-xl border border-[#e2ded4] p-7 space-y-5" data-testid="contact-details-card">
              <h2 className="font-heading text-xl font-bold text-[#0d1f18]">{COMPANY}</h2>
              <a href={PHONE_TEL} data-testid="contact-click-to-call"
                className="flex items-center gap-3 text-sm text-[#2d3748] hover:text-[#1b4332]">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#d8f3dc] text-[#1b4332] shrink-0">
                  <Phone className="w-4 h-4" />
                </span>
                {PHONE_DISPLAY}
              </a>
              <a href={`mailto:${EMAIL}`} data-testid="contact-click-to-email"
                className="flex items-center gap-3 text-sm text-[#2d3748] hover:text-[#1b4332] break-all">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#d8f3dc] text-[#1b4332] shrink-0">
                  <Mail className="w-4 h-4" />
                </span>
                {EMAIL}
              </a>
              <div className="flex items-center gap-3 text-sm text-[#2d3748]">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#d8f3dc] text-[#1b4332] shrink-0">
                  <Globe className="w-4 h-4" />
                </span>
                {WEBSITE}
              </div>
              <div className="flex items-start gap-3 text-sm text-[#2d3748]" data-testid="contact-address">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#d8f3dc] text-[#1b4332] shrink-0">
                  <MapPin className="w-4 h-4" />
                </span>
                {ADDRESS}
              </div>
              <a
                href={waLink("Hello Organik Things, I would like to make a business enquiry.")}
                target="_blank" rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb457] text-white font-semibold px-6 py-3 rounded-lg transition-colors w-full justify-center"
              >
                <MessageCircle className="w-5 h-5" /> Enquire on WhatsApp
              </a>
              <Link
                to="/dealership"
                data-testid="contact-dealer-cta"
                className="inline-flex items-center gap-2 text-[#1b4332] border border-[#1b4332]/30 font-semibold px-6 py-3 rounded-lg hover:bg-[#e8f5e9] transition-colors w-full justify-center"
              >
                <Handshake className="w-5 h-5" /> Enquire About Dealership
              </Link>
            </div>
          </div>
          <Reveal>
            <EnquiryForm mode="contact" defaultProduct={product} testidPrefix="contact" key={product} />
          </Reveal>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-[#faf9f6] py-16 md:py-20" data-testid="contact-faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold tracking-wider uppercase text-[#2d6a4f] mb-3 text-center">FAQs</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0d1f18] text-center">
              Common business questions
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <Accordion type="single" collapsible className="mt-10 bg-white rounded-xl border border-[#e2ded4] px-6">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-heading font-semibold text-[#0d1f18] text-sm sm:text-base" data-testid={`faq-question-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#5a6b5c] leading-relaxed" data-testid={`faq-answer-${i}`}>
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
